import {
  ActionError,
  AttentionBlock,
  Button,
  ChoiceRow,
  ConfirmDialog,
  EmptyState,
  FilterSegments,
  PageHeader,
  PageTitle,
  Radio,
  RadioGroup,
  ReadingColumn,
  Skeleton,
  plural,
  titleCase,
  toast,
  useBreakpoint,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import {
  useMergePatients,
  usePatientDuplicates,
  type DuplicateGroup,
  type DuplicatePatient,
} from './api'
import { displayWhatsapp, isoToBrDate, joinList, matchKeyTitle } from './labels'
import { MobileActionBar } from './mobile-action'

/**
 * Unificar cadastros duplicados (docs/areas/pacientes.md, fluxo "Unificar duplicados"; RN-PAC-16
 * e 17). Arquétipo: fluxo em etapas, de uma decisão só: qual cadastro fica. Os cadastros do grupo
 * aparecem lado a lado, com o principal sugerido já marcado; a unificação é de gestora e root,
 * não tem volta e não refaz nada do que foi assinado.
 */

/** A API unifica de 2 a 5 cadastros por vez: o principal e até quatro absorvidos. */
const MAX_ABSORBED = 4

export function PatientDuplicatesPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const isDesk = useBreakpoint('desk')
  const query = usePatientDuplicates(id)
  const merge = useMergePatients()
  const groups = query.data?.data ?? []

  const [groupIndex, setGroupIndex] = useState(0)
  // O principal escolhido em cada grupo; sem escolha, vale o sugerido pela API.
  const [chosen, setChosen] = useState<Record<number, string>>({})
  const [confirming, setConfirming] = useState(false)

  const index = Math.min(groupIndex, Math.max(groups.length - 1, 0))
  const group: DuplicateGroup | undefined = groups[index]
  const primaryId = group ? (chosen[index] ?? group.suggestedPrimaryId) : null
  const primary = group?.patients.find((patient) => patient.id === primaryId) ?? group?.patients[0]
  const absorbed = group
    ? group.patients.filter((patient) => patient.id !== primary?.id).slice(0, MAX_ABSORBED)
    : []
  const mergedCount = absorbed.length + 1
  const leftOut = group ? group.patients.length - mergedCount : 0

  const canUnify = Boolean(primary) && absorbed.length > 0

  const current = groups.flatMap((item) => item.patients).find((patient) => patient.id === id)
  const currentName = current ? titleCase(current.name) : ''
  const record = routes.patient(id)

  function unify() {
    if (!primary) return
    merge.mutate(
      { primaryId: primary.id, absorbedIds: absorbed.map((patient) => patient.id) },
      {
        onSuccess: (patient) => {
          setConfirming(false)
          toast(`${mergedCount} cadastros unificados em ${titleCase(patient.name)}.`)
          navigate(routes.patient(patient.id))
        },
        onError: () => setConfirming(false),
      },
    )
  }

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Pacientes', href: routes.patients },
          { label: currentName || '…', href: record },
          { label: 'unificar cadastros' },
        ]}
        actions={
          <>
            <Button variant="outline" size="sm" asChild>
              <Link to={record}>Voltar</Link>
            </Button>
            {isDesk && canUnify ? (
              <Button size="sm" onClick={() => setConfirming(true)}>
                Unificar {mergedCount} cadastros
              </Button>
            ) : null}
          </>
        }
      />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title="Unificar cadastros"
            subtitle="Escolha o cadastro que fica. As anamneses, os documentos e os planos dos outros passam para ele; nada do que foi assinado é refeito."
          />

          {query.isError ? (
            <LoadError
              className="mt-6"
              title="Não conseguimos carregar os cadastros duplicados"
              error={query.error}
              onRetry={() => void query.refetch()}
            />
          ) : query.isPending ? (
            <div
              role="status"
              aria-label="Carregando"
              className="tablet:grid-cols-2 mt-6 grid gap-2"
            >
              <Skeleton className="h-[128px] w-full" />
              <Skeleton className="h-[128px] w-full" />
            </div>
          ) : !group || !primary ? (
            <EmptyState
              className="mt-6"
              title="Nenhum cadastro duplicado"
              description="Este cadastro não divide CPF, documento estrangeiro nem WhatsApp com outro cadastro ativo da clínica. Nome parecido não conta como duplicado."
              action={
                <Button variant="outline" asChild>
                  <Link to={record}>Voltar à ficha</Link>
                </Button>
              }
            />
          ) : (
            <div className="mt-6 flex flex-col gap-4">
              {groups.length > 1 ? (
                <FilterSegments
                  label="Grupos de duplicados"
                  options={groups.map((item, position) => ({
                    value: String(position),
                    label: matchKeyTitle(item.matchKey),
                    count: item.patients.length,
                  }))}
                  value={String(index)}
                  onChange={(value) => setGroupIndex(Number(value))}
                />
              ) : null}

              {merge.isError ? (
                <ActionError
                  title="Não foi possível unificar"
                  description={`${errorMessage(merge.error)} Nenhum cadastro mudou.`}
                  onDismiss={() => merge.reset()}
                />
              ) : null}

              <div>
                <h2 className="eyebrow">
                  {matchKeyTitle(group.matchKey)} · {group.value}
                </h2>
                <RadioGroup
                  aria-label="Cadastro que fica"
                  className="tablet:grid-cols-2 mt-2"
                  value={primary.id}
                  onValueChange={(value) => setChosen({ ...chosen, [index]: value })}
                >
                  {group.patients.map((patient) => (
                    <ChoiceRow
                      key={patient.id}
                      className="items-start"
                      control={<Radio value={patient.id} className="mt-1" />}
                      description={<RecordSummary patient={patient} />}
                    >
                      {titleCase(patient.name)}
                      {patient.id === group.suggestedPrimaryId ? ' · sugerido' : ''}
                    </ChoiceRow>
                  ))}
                </RadioGroup>
              </div>

              <AttentionBlock variant="lilac">
                O sugerido é o cadastro com mais documentos assinados e, no empate, o mais antigo.
                Os campos vazios de quem fica recebem o que os outros tiverem; o que já está
                preenchido prevalece.
                {leftOut > 0
                  ? ` A unificação cobre 5 cadastros por vez: ${plural(leftOut, 'cadastro fica', 'cadastros ficam')} para a próxima.`
                  : ''}
              </AttentionBlock>
            </div>
          )}
        </ReadingColumn>
      </Page>

      {!isDesk && canUnify ? (
        <MobileActionBar>
          <Button size="lg" onClick={() => setConfirming(true)}>
            Unificar {mergedCount} cadastros
          </Button>
        </MobileActionBar>
      ) : null}

      <ConfirmDialog
        open={confirming && Boolean(primary)}
        onOpenChange={setConfirming}
        title={`Unificar os ${mergedCount} cadastros de "${primary ? titleCase(primary.name) : ''}"?`}
        effect={primary ? mergeEffect(primary, absorbed) : ''}
        alternative={{
          description:
            'Em dúvida se é a mesma pessoa? Confira os cadastros lado a lado antes de unificar.',
          label: mergedCount === 2 ? 'Ver os dois cadastros' : `Ver os ${mergedCount} cadastros`,
          onSelect: () => setConfirming(false),
        }}
        confirmLabel="Unificar"
        loading={merge.isPending}
        loadingLabel="Unificando…"
        onConfirm={unify}
      />
    </>
  )
}

/** O resumo de um cadastro do grupo: o bastante para dizer qual deles fica. */
function RecordSummary({ patient }: { patient: DuplicatePatient }) {
  const { counts } = patient
  return (
    <>
      <span className="block">
        paciente desde {isoToBrDate(patient.patientSince)} · cadastro {patient.completeness.percent}
        % completo
      </span>
      <span className="block">
        {[displayWhatsapp(patient.whatsapp), patient.email].filter(Boolean).join(' · ') ||
          'sem contato'}
      </span>
      <span className="block">
        {plural(counts.anamneses, 'anamnese')} ·{' '}
        {plural(counts.signedDocumentCount, 'documento assinado', 'documentos assinados')} de{' '}
        {counts.documents} · {plural(counts.treatmentPlans, 'plano')}
      </span>
    </>
  )
}

/** O efeito real e o que não se perde, com os números de todos os cadastros do grupo. */
function mergeEffect(primary: DuplicatePatient, absorbed: DuplicatePatient[]) {
  const all = [primary, ...absorbed]
  const sum = (pick: (patient: DuplicatePatient) => number) =>
    all.reduce((total, patient) => total + pick(patient), 0)
  const kept = [
    plural(
      sum((patient) => patient.counts.signedDocumentCount),
      'documento assinado',
      'documentos assinados',
    ),
    plural(
      sum((patient) => patient.counts.anamneses),
      'anamnese',
    ),
    plural(
      sum((patient) => patient.counts.treatmentPlans),
      'plano',
    ),
  ]
  return `O cadastro de ${isoToBrDate(primary.patientSince)} passa a ser o único. Continuam intactos, agora numa ficha só: ${joinList(kept)}. Não dá para desfazer.`
}
